import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React, { useMemo, useContext, useEffect, useState } from 'react';
import { IntlContext } from '@paypalcorp/pp-react-intl';
import { TextInput } from '@paypalcorp/pp-react-text-input';
import { Combobox } from '@paypalcorp/pp-react-combobox';
import { Col, Row } from '@paypalcorp/pp-react-grid';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { SuggestionList, AutoSuggest } from '@paypalcorp/pp-react-auto-suggest';
import { BodyText, CaptionText } from '@paypalcorp/pp-react-text';
import { highlightMatch, devWarning } from '@paypalcorp/pp-react-utils';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';

var _templateObject, _templateObject2, _templateObject3;
function styles() {
  var tokens = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var sysColorBackgroundMediumContrast = tokens.sysColorBackgroundMediumContrast,
      sysSpaceXs = tokens.sysSpaceXs;
  var suggestionListFooter = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: suggestion_list_footer;\n    text-align: center;\n  "])));
  var googleLogo = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    height: ", ";\n  "])), '0.75rem');
  var googleAddressSecondaryText = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: google_address_secondary_styles;\n    color: ", ";\n    margin-top: ", ";\n  "])), sysColorBackgroundMediumContrast, sysSpaceXs);
  return {
    suggestionListFooter: suggestionListFooter,
    googleLogo: googleLogo,
    googleAddressSecondaryText: googleAddressSecondaryText
  };
}

var GoogleAddressSuggestion = function (_ref) {
  var _ref$mainText = _ref.mainText,
      mainText = _ref$mainText === void 0 ? 'Main Text' : _ref$mainText,
      _ref$secondaryText = _ref.secondaryText,
      secondaryText = _ref$secondaryText === void 0 ? 'Secondary Text' : _ref$secondaryText,
      query = _ref.query,
      className = _ref.className;
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var highlightedMainText = highlightMatch(mainText, query);
  return jsx("div", {
    className: className,
    "data-ppui": "true"
  }, jsx(BodyText, {
    dangerouslySetInnerHTML: {
      __html: highlightedMainText
    },
    "data-ppui": "true"
  }), jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref2) {
    var css = _ref2.css;
    return jsx(CaptionText, {
      className: css(_styles.googleAddressSecondaryText),
      "data-ppui": "true"
    }, secondaryText);
  }));
};

var GoogleFooter = function (_ref) {
  var _ref$domain = _ref.domain,
      domain = _ref$domain === void 0 ? 'www.paypalobjects.com' : _ref$domain,
      className = _ref.className;

  var _styles = styles();

  return jsx("div", {
    css: _styles.suggestionListFooter,
    className: className,
    "data-ppui": "true"
  }, jsx("img", {
    css: _styles.googleLogo,
    src: "https://".concat(domain, "/paypal-ui/web/components/address-input/powered_by_google_on_white.png"),
    alt: "Powered by Google",
    "data-ppui": "true"
  }));
};

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/**
 * Renders suggestion from Google Places Autocomplete service https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompletePrediction
 * suggestion: {mainText, secondaryText, description, placeId, matchedSubstrings}
 * @param suggestion
 * @returns
 */

/* istanbul ignore next */

function renderGoogleAddressSuggestion(suggestion, query) {
  var mainText = suggestion.mainText,
      secondaryText = suggestion.secondaryText;
  return /*#__PURE__*/React.createElement(GoogleAddressSuggestion, {
    mainText: mainText,
    secondaryText: secondaryText,
    query: query,
    "data-ppui": "true"
  });
}
/**
 * Validation hook to validate using AddressFormatter from IntlContext
 */


function useValidate(_ref) {
  var getAddress = _ref.getAddress,
      onValidate = _ref.onValidate,
      validate = _ref.validate,
      region = _ref.region,
      requiredFieldErrorText = _ref.requiredFieldErrorText,
      invalidFieldErrorText = _ref.invalidFieldErrorText;
  var intlContext = useContext(IntlContext);
  var validator = region && intlContext && intlContext.addressFormatter && intlContext.addressFormatter({
    addressRegion: region
  });

  var validateAddress = function () {
    var validations = [];
    var errors = {};
    var address = getAddress();
    address.countryCode = region;
    validations = validator.validate(address);
    validations.forEach(function (_ref2) {
      var code = _ref2.code,
          element = _ref2.element;

      /* istanbul ignore next */
      errors[element] = code === 'MISSING_REQUIRED' ? requiredFieldErrorText : invalidFieldErrorText;
    });
    onValidate(errors, validations);
  };

  useEffect(function () {
    if (validate && validator) {
      validateAddress();
    }
  }, [validate]);
}
/**
 * Address state hook
 */


function useAddress() {
  var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref3$initialAddress = _ref3.initialAddress,
      initialAddress = _ref3$initialAddress === void 0 ? {
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    adminArea1: '',
    adminArea2: '',
    adminArea3: '',
    adminArea4: '',
    postalCode: ''
  } : _ref3$initialAddress,
      _ref3$initialAddressE = _ref3.initialAddressErrors,
      initialAddressErrors = _ref3$initialAddressE === void 0 ? {} : _ref3$initialAddressE,
      _ref3$autoSuggestFiel = _ref3.autoSuggestField,
      autoSuggestField = _ref3$autoSuggestFiel === void 0 ? 'addressLine1' : _ref3$autoSuggestFiel,
      suggestions = _ref3.suggestions,
      fetchSuggestions = _ref3.fetchSuggestions,
      onSuggestionSelect = _ref3.onSuggestionSelect,
      _ref3$renderSuggestio = _ref3.renderSuggestion,
      renderSuggestion = _ref3$renderSuggestio === void 0 ? renderGoogleAddressSuggestion : _ref3$renderSuggestio,
      _ref3$suggestionListF = _ref3.suggestionListFooter,
      suggestionListFooter = _ref3$suggestionListF === void 0 ? /*#__PURE__*/React.createElement(GoogleFooter, {
    "data-ppui": "true"
  }) : _ref3$suggestionListF;

  var _useState = useState(initialAddress.addressLine1),
      _useState2 = _slicedToArray(_useState, 2),
      addressLine1 = _useState2[0],
      setAddressLine1 = _useState2[1];

  var _useState3 = useState(initialAddress.addressLine2),
      _useState4 = _slicedToArray(_useState3, 2),
      addressLine2 = _useState4[0],
      setAddressLine2 = _useState4[1];

  var _useState5 = useState(initialAddress.addressLine3),
      _useState6 = _slicedToArray(_useState5, 2),
      addressLine3 = _useState6[0],
      setAddressLine3 = _useState6[1];

  var _useState7 = useState(initialAddress.adminArea1),
      _useState8 = _slicedToArray(_useState7, 2),
      adminArea1 = _useState8[0],
      setAdminArea1 = _useState8[1];

  var _useState9 = useState(initialAddress.adminArea2),
      _useState10 = _slicedToArray(_useState9, 2),
      adminArea2 = _useState10[0],
      setAdminArea2 = _useState10[1];

  var _useState11 = useState(initialAddress.adminArea3),
      _useState12 = _slicedToArray(_useState11, 2),
      adminArea3 = _useState12[0],
      setAdminArea3 = _useState12[1];

  var _useState13 = useState(initialAddress.adminArea4),
      _useState14 = _slicedToArray(_useState13, 2),
      adminArea4 = _useState14[0],
      setAdminArea4 = _useState14[1];

  var _useState15 = useState(initialAddress.postalCode),
      _useState16 = _slicedToArray(_useState15, 2),
      postalCode = _useState16[0],
      setPostalCode = _useState16[1];

  var _useState17 = useState(initialAddressErrors.addressLine1),
      _useState18 = _slicedToArray(_useState17, 2),
      addressLine1Error = _useState18[0],
      setAddressLine1Error = _useState18[1];

  var _useState19 = useState(initialAddressErrors.addressLine2),
      _useState20 = _slicedToArray(_useState19, 2),
      addressLine2Error = _useState20[0],
      setAddressLine2Error = _useState20[1];

  var _useState21 = useState(initialAddressErrors.addressLine3),
      _useState22 = _slicedToArray(_useState21, 2),
      addressLine3Error = _useState22[0],
      setAddressLine3Error = _useState22[1];

  var _useState23 = useState(initialAddressErrors.adminArea1),
      _useState24 = _slicedToArray(_useState23, 2),
      adminArea1Error = _useState24[0],
      setAdminArea1Error = _useState24[1];

  var _useState25 = useState(initialAddressErrors.adminArea2),
      _useState26 = _slicedToArray(_useState25, 2),
      adminArea2Error = _useState26[0],
      setAdminArea2Error = _useState26[1];

  var _useState27 = useState(initialAddressErrors.adminArea3),
      _useState28 = _slicedToArray(_useState27, 2),
      adminArea3Error = _useState28[0],
      setAdminArea3Error = _useState28[1];

  var _useState29 = useState(initialAddressErrors.adminArea4),
      _useState30 = _slicedToArray(_useState29, 2),
      adminArea4Error = _useState30[0],
      setAdminArea4Error = _useState30[1];

  var _useState31 = useState(initialAddressErrors.postalCode),
      _useState32 = _slicedToArray(_useState31, 2),
      postalCodeError = _useState32[0],
      setPostalCodeError = _useState32[1];
  /* istanbul ignore next */


  var setError = function (field, error) {
    switch (field) {
      case 'addressLine1':
        setAddressLine1Error(error);
        break;

      case 'addressLine2':
        setAddressLine2Error(error);
        break;

      case 'addressLine3':
        setAddressLine3Error(error);
        break;

      case 'adminArea1':
        setAdminArea1Error(error);
        break;

      case 'adminArea2':
        setAdminArea2Error(error);
        break;

      case 'adminArea3':
        setAdminArea3Error(error);
        break;

      case 'adminArea4':
        setAdminArea4Error(error);
        break;

      case 'postalCode':
        setPostalCodeError(error);
        break;
    }
  };
  /* istanbul ignore next */


  var handleChange = function (evt, field) {
    var value = evt.target.value;
    setError(field, ''); // reset any error

    switch (field) {
      case 'addressLine1':
        setAddressLine1(value);
        break;

      case 'addressLine2':
        setAddressLine2(value);
        break;

      case 'adminArea1':
        setAdminArea1(value);
        break;

      case 'adminArea2':
        setAdminArea2(value);
        break;

      case 'adminArea3':
        setAdminArea3(value);
        break;

      case 'adminArea4':
        setAdminArea4(value);
        break;

      case 'postalCode':
        setPostalCode(value);
        break;
    }
  };

  var getAddress = function () {
    return {
      addressLine1: addressLine1,
      addressLine2: addressLine2,
      addressLine3: addressLine3,
      adminArea1: adminArea1,
      adminArea2: adminArea2,
      adminArea3: adminArea3,
      adminArea4: adminArea4,
      postalCode: postalCode
    };
  };
  /* istanbul ignore next */


  var setAddress = function (address) {
    setAddressLine1(address.addressLine1 || '');
    setAddressLine2(address.addressLine2 || '');
    setAddressLine3(address.addressLine3 || '');
    setAdminArea1(address.adminArea1 || '');
    setAdminArea2(address.adminArea2 || '');
    setAdminArea3(address.adminArea3 || '');
    setAdminArea4(address.adminArea4 || '');
    setPostalCode(address.postalCode || '');
  };
  /* istanbul ignore next */


  var addressParts = {
    addressLine1: {
      props: {
        value: addressLine1,
        errorText: addressLine1Error,
        onChange: function onChange(evt) {
          handleChange(evt, 'addressLine1');
        }
      }
    },
    addressLine2: {
      props: {
        value: addressLine2,
        errorText: addressLine2Error,
        onChange: function onChange(evt) {
          return handleChange(evt, 'addressLine2');
        }
      }
    },
    addressLine3: {
      props: {
        value: addressLine3,
        errorText: addressLine3Error,
        onChange: function onChange(evt) {
          return handleChange(evt, 'addressLine3');
        }
      }
    },
    adminArea1: {
      props: {
        value: adminArea1,
        errorText: adminArea1Error,
        onChange: function onChange(evt) {
          return handleChange(evt, 'adminArea1');
        }
      }
    },
    adminArea2: {
      props: {
        value: adminArea2,
        errorText: adminArea2Error,
        onChange: function onChange(evt) {
          return handleChange(evt, 'adminArea2');
        }
      }
    },
    adminArea3: {
      props: {
        value: adminArea3,
        errorText: adminArea3Error,
        onChange: function onChange(evt) {
          return handleChange(evt, 'adminArea3');
        }
      }
    },
    adminArea4: {
      props: {
        value: adminArea4,
        errorText: adminArea4Error,
        onChange: function onChange(evt) {
          return handleChange(evt, 'adminArea4');
        }
      }
    },
    postalCode: {
      props: {
        value: postalCode,
        errorText: postalCodeError,
        onChange: function onChange(evt) {
          return handleChange(evt, 'postalCode');
        }
      }
    }
  };

  if (fetchSuggestions) {
    var suggestionList = /*#__PURE__*/React.createElement(SuggestionList, {
      suggestions: suggestions,
      query: addressParts[autoSuggestField].props.value,
      renderSuggestion: renderSuggestion,
      onSelect: onSuggestionSelect,
      "data-ppui": "true"
    });
    addressParts[autoSuggestField].as = AutoSuggest;
    addressParts[autoSuggestField].props = _objectSpread(_objectSpread({}, addressParts[autoSuggestField].props), {}, {
      onClear: function onClear() {
        var currentAddress = getAddress();
        currentAddress[autoSuggestField] = '';
        setAddress(currentAddress);
      },
      fetchSuggestions: fetchSuggestions,
      suggestionList: suggestionList,
      suggestions: suggestions,
      suggestionListFooter: suggestionListFooter
    });
  }

  return {
    addressLine1: addressLine1,
    addressLine2: addressLine2,
    addressLine3: addressLine3,
    adminArea1: adminArea1,
    adminArea2: adminArea2,
    adminArea3: adminArea3,
    adminArea4: adminArea4,
    postalCode: postalCode,
    addressLine1Error: addressLine1Error,
    addressLine2Error: addressLine2Error,
    addressLine3Error: addressLine3Error,
    adminArea1Error: adminArea1Error,
    adminArea2Error: adminArea2Error,
    adminArea3Error: adminArea3Error,
    adminArea4Error: adminArea4Error,
    postalCodeError: postalCodeError,
    addressParts: addressParts,
    handleChange: handleChange,
    setError: setError,
    getAddress: getAddress,
    setAddress: setAddress
  };
}

var AddressInputUtils = {
  useValidate: useValidate,
  useAddress: useAddress
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var REQ_FIELD_ERROR = 'Please enter a value';
var INVALID_FIELD_ERROR = 'Please enter a valid value';

function getAddressFromParts(parts) {
  return Object.keys(parts).reduce(function (acc, key) {
    /* istanbul ignore next */
    acc[key] = parts[key].props.value || '';
    return acc;
  }, {});
}

// Get address parts to display for the current locale and address region from IntlProvider
function getAddressParts(intlContext, region) {
  var partsToDisplay = {};
  var partsDisplayOrder = [];

  if (region && intlContext && intlContext.addressFormatter) {
    var addressParts = intlContext.addressFormatter({
      addressRegion: region
    }).formatToParts({});
    addressParts.forEach(function (_ref) {
      var parts = _ref.parts;
      var types = [];
      parts.forEach(function (_ref2) {
        var allowedValues = _ref2.allowedValues,
            type = _ref2.type;
        partsToDisplay[type] = {};
        partsToDisplay[type].props = {};
        types.push(type);
        /* istanbul ignore else */

        if (intlContext.getDisplayNamesObject) {
          if (allowedValues) {
            var translations = intlContext.getDisplayNamesObject({
              type: 'portableAddressValues',
              element: type,
              addressRegion: region
            });
            var options = allowedValues.map(function (allowedValue) {
              return {
                label: translations.select(allowedValue),
                value: allowedValue
              };
            });
            partsToDisplay[type].props.options = options;
            partsToDisplay[type].as = Combobox;
          }

          var labelTranslations = intlContext.getDisplayNamesObject({
            type: 'portableAddress',
            addressRegion: region
          });
          partsToDisplay[type].props.label = labelTranslations.select(type);
        }
      });

      if (types.length > 1) {
        partsDisplayOrder.push(types);
      } else {
        partsDisplayOrder.push(types[0]);
      }
    });
  }

  return {
    partsToDisplay: partsToDisplay,
    partsDisplayOrder: partsDisplayOrder
  };
}

var AddressInput = function (_ref3) {
  var region = _ref3.region,
      _ref3$parts = _ref3.parts,
      parts = _ref3$parts === void 0 ? {} : _ref3$parts,
      partsOrder = _ref3.partsOrder,
      _ref3$excludedParts = _ref3.excludedParts,
      excludedParts = _ref3$excludedParts === void 0 ? [] : _ref3$excludedParts,
      className = _ref3.className,
      fieldClassName = _ref3.fieldClassName,
      fieldNamePrefix = _ref3.fieldNamePrefix,
      validate = _ref3.validate,
      _ref3$requiredFieldEr = _ref3.requiredFieldErrorText,
      requiredFieldErrorText = _ref3$requiredFieldEr === void 0 ? REQ_FIELD_ERROR : _ref3$requiredFieldEr,
      _ref3$invalidFieldErr = _ref3.invalidFieldErrorText,
      invalidFieldErrorText = _ref3$invalidFieldErr === void 0 ? INVALID_FIELD_ERROR : _ref3$invalidFieldErr,
      onValidate = _ref3.onValidate;
  AddressInputUtils.useValidate({
    getAddress: function getAddress() {
      return getAddressFromParts(parts);
    },
    onValidate: onValidate,
    validate: validate,
    region: region,
    requiredFieldErrorText: requiredFieldErrorText,
    invalidFieldErrorText: invalidFieldErrorText
  });
  var intlContext = useContext(IntlContext);
  var _partsOrder = partsOrder;

  var _useMemo = useMemo(function () {
    return getAddressParts(intlContext, region);
  }, [region, intlContext]),
      partsToDisplay = _useMemo.partsToDisplay,
      partsDisplayOrder = _useMemo.partsDisplayOrder;

  if (region && !(_partsOrder && _partsOrder.length)) {
    _partsOrder = partsDisplayOrder;
  }

  if (!(_partsOrder && _partsOrder.length)) {
    _partsOrder = Object.keys(parts);
  }

  var tempPartsOrder = [];

  _partsOrder.forEach(function (part) {
    if (part.length > 6 && typeof part !== 'string') {
      for (var i = 0; i < part.length; i += 4) {
        tempPartsOrder.push(part.slice(i, i + 4));
      }
    } else {
      tempPartsOrder.push(part);
    }
  });

  _partsOrder = tempPartsOrder;

  var renderPart = function (part) {
    var partObj = _objectSpread$1(_objectSpread$1({
      props: {}
    }, partsToDisplay[part]), parts[part]); //Merge props


    if (partsToDisplay[part] && parts[part]) {
      partObj.props = _objectSpread$1(_objectSpread$1({}, partsToDisplay[part].props), parts[part].props);
    }

    if (!partObj.as) {
      if (partObj.props.options) {
        partObj.as = Combobox;
      } else {
        partObj.as = TextInput;
      }
    }

    var Component = partObj.as;
    return /*#__PURE__*/React.createElement(Component, _extends({
      name: "".concat(fieldNamePrefix, "_").concat(part)
    }, partObj.props, {
      "data-ppui": "true"
    }));
  };

  var rows = [];

  _partsOrder.forEach(function (part, index) {
    var columns = [];

    if (typeof part !== 'string') {
      var cols = Math.floor(12 / part.length);
      part.forEach(function (subPart, colIndex) {
        if (!excludedParts.includes(subPart)) {
          columns.push( /*#__PURE__*/React.createElement(Col, {
            sm: cols,
            xs: 12,
            className: fieldClassName,
            key: colIndex,
            form: "vspace",
            "data-ppui": "true"
          }, renderPart(subPart)));
        }
      });
    } else if (!excludedParts.includes(part)) {
      columns.push( /*#__PURE__*/React.createElement(Col, {
        xs: 12,
        className: fieldClassName,
        key: index,
        form: "vspace",
        "data-ppui": "true"
      }, renderPart(part)));
    }

    if (columns.length) {
      rows.push( /*#__PURE__*/React.createElement(Row, {
        key: index,
        "data-ppui": "true"
      }, columns));
    }
  });

  return /*#__PURE__*/React.createElement("div", {
    className: className,
    "data-ppui-info": "address-input_2.4.13"
  }, rows);
};

var _excluded = ["region", "validate", "parts", "requiredFieldErrorText", "invalidFieldErrorText", "address", "addressErrors", "onChange", "onValidate"];

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var ADDRESS_PARTS = ['recipient', 'businessName', 'addressLine1', 'addressLine2', 'addressLine3', 'adminArea1', 'adminArea2', 'adminArea3', 'adminArea4', 'postalCode'];

var AddressInputWithValidation = function (_ref) {
  var region = _ref.region,
      validate = _ref.validate,
      _ref$parts = _ref.parts,
      parts = _ref$parts === void 0 ? {} : _ref$parts,
      _ref$requiredFieldErr = _ref.requiredFieldErrorText,
      requiredFieldErrorText = _ref$requiredFieldErr === void 0 ? 'Please enter a value' : _ref$requiredFieldErr,
      _ref$invalidFieldErro = _ref.invalidFieldErrorText,
      invalidFieldErrorText = _ref$invalidFieldErro === void 0 ? 'Please enter a valid value' : _ref$invalidFieldErro,
      _ref$address = _ref.address,
      address = _ref$address === void 0 ? {
    addressLine1: ''
  } : _ref$address,
      _ref$addressErrors = _ref.addressErrors,
      addressErrors = _ref$addressErrors === void 0 ? {} : _ref$addressErrors,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function () {
    return devWarning('onChange prop is missing');
  } : _ref$onChange,
      onValidate = _ref.onValidate,
      rest = _objectWithoutProperties(_ref, _excluded);

  AddressInputUtils.useValidate({
    getAddress: function getAddress() {
      return address;
    },
    onValidate: onValidate,
    validate: validate,
    region: region,
    requiredFieldErrorText: requiredFieldErrorText,
    invalidFieldErrorText: invalidFieldErrorText
  });

  var handleChange = function (evt, part) {
    onChange(_objectSpread$2(_objectSpread$2({}, address), {}, _defineProperty({}, part, evt.target.value)));
  };

  var _parts = useMemo(function () {
    return ADDRESS_PARTS.reduce(function (acc, part) {
      acc[part] = _objectSpread$2({
        props: {}
      }, parts[part]); // adminArea1 uses Combobox -- we need to handle both text and click changes

      /* istanbul ignore next */

      if (part === 'adminArea1') {
        acc[part].props.onTextChange = function (evt) {
          return handleChange(evt, part);
        };
      }

      acc[part].props.onChange = function (evt) {
        return handleChange(evt, part);
      };

      acc[part].props.value = address[part] || '';
      acc[part].props.errorText = addressErrors[part];
      return acc;
    }, {});
  }, [parts]);

  return /*#__PURE__*/React.createElement(AddressInput, _extends({
    parts: _parts,
    region: region,
    "data-ppui-info": "address-input_2.4.13"
  }, rest));
};

export { AddressInput, AddressInputUtils, AddressInputWithValidation, GoogleAddressSuggestion, GoogleFooter, styles as addressInputStyles };
